import React, { useEffect, useState } from 'react';
import { lightenColor, ReMixChart } from '../../components/recharts/ReMixCharts';
import { BrushStart, ChartType, LegendKey } from '../../models/chart_model';
import { COLOR_CODES, formatDMY } from '../../common';
import { FilterTab } from '../../components/Filters';
import { ChartTable } from '../../components/table/SummaryTable';
import { ChartData, ChartDataParams, FormatChartData } from '../../components/recharts/functions';
import { titleCase } from '../../extensions/extension';
import { ConsumptionData } from '../../models/consumption';
import { GenerationData } from '../../models/generationData';
import { getConsumptionData, getGenerationData } from '../../Rest_api/RestApi';

const views = ['Chart', 'Table'];
const tabs = ['Trend ', 'Source-Wise'];

export function ConsumptionGenerationChart({ startDate, endDate, isFullscreen = false }: { startDate: string, endDate: string, isFullscreen?: boolean }) {
  const [consumptionData, setConsumptionData] = useState<ConsumptionData[]>([]);
  const [generationData, setGenerationData] = useState<GenerationData[]>([]);
  const [prevYearConsumptionData, setPrevYearConsumptionData] = useState<ConsumptionData[]>([]);
  const [prevYearGenerationData, setPrevYearGenerationData] = useState<GenerationData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedView, setSelectedView] = useState(views[0]);
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [trendChartData, setTrendChartData] = useState<ChartData>({
    keys: [],
    unit_keys: [],
    keysLabels: [],
    group_by: {},
    data: [],
  });
  const [sourceWiseChartData, setSourceWiseChartData] = useState<ChartData>({
    keys: [],
    unit_keys: [],
    keysLabels: [],
    group_by: {},
    data: [],
  });

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Helper function to check if a year is a leap year
      const isLeapYear = (year: number): boolean => {
        return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
      };

      // Calculate previous year dates - simple approach that handles leap years correctly
      const currentStartDate = new Date(startDate);
      const currentEndDate = new Date(endDate);
      const currentYear = currentStartDate.getFullYear();
      const prevYear = currentYear - 1;
      
      // Simple approach: just subtract 1 year and let JavaScript handle leap years
      const prevYearStartDate = new Date(currentStartDate);
      prevYearStartDate.setFullYear(prevYear);
      
      const prevYearEndDate = new Date(currentEndDate);
      prevYearEndDate.setFullYear(prevYear);
      
      // Handle the case where Feb 29th doesn't exist in the previous year
      if (currentStartDate.getMonth() === 1 && currentStartDate.getDate() === 29 && 
          !isLeapYear(prevYear) && isLeapYear(currentYear)) {
        prevYearStartDate.setDate(28);
      }
      if (currentEndDate.getMonth() === 1 && currentEndDate.getDate() === 29 && 
          !isLeapYear(prevYear) && isLeapYear(currentYear)) {
        prevYearEndDate.setDate(28);
      }
      
      // Debug logging
      console.log('Date calculation debug:', {
        currentStartDate: currentStartDate.toISOString().split('T')[0],
        currentEndDate: currentEndDate.toISOString().split('T')[0],
        prevYearStartDate: prevYearStartDate.toISOString().split('T')[0],
        prevYearEndDate: prevYearEndDate.toISOString().split('T')[0],
        currentYear,
        prevYear,
        isCurrentLeapYear: isLeapYear(currentYear),
        isPrevLeapYear: isLeapYear(prevYear)
      });

      const [consumption, generation, prevConsumption, prevGeneration] = await Promise.all([
        getConsumptionData(startDate, endDate),
        getGenerationData(startDate, endDate),
        getConsumptionData(prevYearStartDate.toISOString().split('T')[0], prevYearEndDate.toISOString().split('T')[0]),
        getGenerationData(prevYearStartDate.toISOString().split('T')[0], prevYearEndDate.toISOString().split('T')[0])
      ]);

      setConsumptionData(consumption);
      setGenerationData(generation);
      setPrevYearConsumptionData(prevConsumption);
      setPrevYearGenerationData(prevGeneration);
    } catch (error) {
      console.error('Error fetching consumption and generation data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTab === 'Trend ') {
      processTrendData();
    } else if (selectedTab === 'Source-Wise') {
      processSourceWiseData();
    }
  }, [consumptionData, generationData, prevYearConsumptionData, prevYearGenerationData, selectedTab]);

    const processTrendData = () => {
    // Helper function to standardize date format to YYYY-MM-DD
    const standardizeDateFormat = (dateStr: string) => {
      if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        if (parts[0].length === 4) {
          // Already in YYYY-MM-DD format
          return dateStr;
        } else {
          // Convert DD-MM-YYYY to YYYY-MM-DD
          const [day, month, year] = parts;
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
      return dateStr;
    };

    // Helper function to check if a year is a leap year
    const isLeapYear = (year: number): boolean => {
      return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
    };

    // Map previous year dates to current year dates for proper comparison
    const mapPrevYearToCurrentYear = (prevData: any[], dataType: string) => {
      return prevData.map(item => {
        // Standardize the date format first
        const standardizedDate = standardizeDateFormat(item.date);
        const prevDate = new Date(standardizedDate);
        
        // Check if date is valid
        if (isNaN(prevDate.getTime())) {
          console.warn('Invalid date found:', item.date);
          return null;
        }
        
        const currentYear = new Date().getFullYear();
        const prevYear = prevDate.getFullYear();
        
        // Calculate the day of year for the previous year date
        const startOfPrevYear = new Date(prevYear, 0, 1);
        const dayOfYear = Math.floor((prevDate.getTime() - startOfPrevYear.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        
        // Calculate the corresponding date in current year
        const startOfCurrentYear = new Date(currentYear, 0, 1);
        const currentDate = new Date(startOfCurrentYear.getTime() + (dayOfYear - 1) * (1000 * 60 * 60 * 24));
        
        // Handle leap year edge cases
        // If the previous year was a leap year and we're on Feb 29th, 
        // and current year is not a leap year, map to Feb 28th
        if (prevDate.getMonth() === 1 && prevDate.getDate() === 29 && 
            !isLeapYear(currentYear) && isLeapYear(prevYear)) {
          currentDate.setDate(28);
        }
        
        // If the previous year was not a leap year and current year is a leap year,
        // and we're on Feb 28th, we could map to Feb 29th, but let's keep it as Feb 28th for consistency
        // (This is optional - you might want to handle this differently based on your business logic)
        
        const formattedDate = currentDate.toISOString().split('T')[0];
        
        return {
          ...item,
          date: formattedDate,
          type: 'previous',
          data_type: dataType,
          value: dataType === 'consumption' ? item.energy_met_mu : item.generation_mu_actual
        };
      }).filter(item => item !== null); // Remove any null items from invalid dates
    };

    // Create combined dataset with type field for current/previous year
    const combinedRawData = [
      // Current year consumption data (standardize date format)
      ...consumptionData.filter((item) => item.states !== 'BHUTAN').map(item => ({
        ...item,
        date: standardizeDateFormat(item.date),
        type: 'current',
        data_type: 'consumption',
        value: item.energy_met_mu
      })),
      // Previous year consumption data (mapped to current year dates)
      ...mapPrevYearToCurrentYear(
        prevYearConsumptionData.filter((item) => item.states !== 'BHUTAN'), 
        'consumption'
      ),
      // Current year generation data (standardize date format)
      ...generationData.filter((item) => item.state__name !== 'BHUTAN').map(item => ({
        ...item,
        date: standardizeDateFormat(item.date),
        type: 'current',
        data_type: 'generation',
        value: item.generation_mu_actual
      })),
      // Previous year generation data (mapped to current year dates)
      ...mapPrevYearToCurrentYear(
        prevYearGenerationData.filter((item) => item.state__name !== 'BHUTAN'), 
        'generation'
      )
    ];

    // Process with FormatChartData
    const par: ChartDataParams = {
      data: combinedRawData,
      keys: ['date'],
      volumeKey: 'value',
      groupBy: ['type', 'data_type'],
      reverseSort: ['date'],
      aggregationKeys: [],
    };
    
    const formattedData = FormatChartData(par);
    
    // Convert to Million MWh
    const convertedData = {
      ...formattedData,
      data: formattedData.data.map(item => {
        const convertedItem = { ...item, };
        Object.keys(convertedItem).forEach(key => {
          if (key.includes('_value') && typeof convertedItem[key] === 'number') {
            convertedItem[key] = convertedItem[key] / 1000;
          }
        });
        convertedItem.date = formatDMY(convertedItem.date);
        return convertedItem;
      })
    };
    
    setTrendChartData(convertedData);
  };

  const processSourceWiseData = () => {
    // Create combined dataset with consumption and generation data
    const combinedRawData = [
      // Consumption data
      ...consumptionData.filter((item) => item.states !== 'BHUTAN').map(item => ({
        ...item,
        date: formatDMY(item.date),
        data_type: 'consumption',
        value: item.energy_met_mu
      })),
      // Generation data by power type
      ...generationData.filter((item) => 
        item.state__name !== 'BHUTAN' && 
        ['thermal', 'hydro', 'nuclear', 'solar', 'wind', 'other'].includes(item.power_type__name.toLowerCase())
      ).map(item => ({
        ...item,
        date: formatDMY(item.date),
        data_type: item.power_type__name.toLowerCase(),
        value: item.generation_mu_actual
      }))
    ];

    // Process with FormatChartData
    const par: ChartDataParams = {
      data: combinedRawData,
      keys: ['date'],
      volumeKey: 'value',
      groupBy: ['data_type'],
      aggregationKeys: [],
    };
    
    const formattedData = FormatChartData(par);
    
    // Convert to Million MWh
    const convertedData = {
      ...formattedData,
      data: formattedData.data.map(item => {
        const convertedItem = { ...item };
        Object.keys(convertedItem).forEach(key => {
          if (key.includes('_value') && typeof convertedItem[key] === 'number') {
            convertedItem[key] = convertedItem[key] / 1000;
          }
        });
        return convertedItem;
      })
    };

    setSourceWiseChartData(convertedData);
  };

  const getTrendLegends = () => {
    return [
      {
        name: 'Generation',
        dataKey: 'current_generation_value',
        stroke: COLOR_CODES.secondary,
        unit: 'Million MWh',
        type: ChartType.Bar
      },
      {
        name: 'Generation (Prev. Year)',
        dataKey: 'previous_generation_value',
        // stroke: lightenColor(COLOR_CODES.secondary, 40), // Lightened shade of secondary color
        stroke: lightenColor(COLOR_CODES.secondary, 30), // Lightened shade of secondary color
        legendColor: lightenColor(COLOR_CODES.secondary, 15),
        unit: 'Million MWh',
        
        type: ChartType.Bar
      },
      {
        name: 'Consumption',
        dataKey: 'current_consumption_value',
        stroke: COLOR_CODES.primary,
        unit: 'Million MWh',
        type: ChartType.Line
      },
      {
        name: 'Consumption (Prev. Year)',
        dataKey: 'previous_consumption_value',
        stroke: lightenColor(COLOR_CODES.primary, 20), // Lightened shade of primary color
        legendColor: "#f7b288",
        unit: 'Million MWh',
        type: ChartType.Line
      }
    ];
  };

    const getSourceWiseLegends = () => {
    const GENERATION_SOURCE = {
      'thermal': COLOR_CODES.primary,
      'hydro': COLOR_CODES.secondary,
      'nuclear': 'green',
      'solar': 'orange',
      'wind': 'blue',
      'other': 'purple',
    };

    const legends: LegendKey[] = [

    ];

    // Add generation sources
    const generationKeys = sourceWiseChartData.keys.filter((key) => key !== 'consumption');
    generationKeys.forEach(key => {
      legends.push({
        name: titleCase(key),
        dataKey: key + '_value',
        stroke: GENERATION_SOURCE[key.toLowerCase() as keyof typeof GENERATION_SOURCE] || 'gray',
        unit: 'Million MWh',
        stackId: 'a',
        type: ChartType.Area,
      } as any);
    });

    legends.push(
        {
            name: 'Total Consumption',
            dataKey: 'consumption_value',
            stroke: COLOR_CODES.secondary,
            unit: 'Million MWh',
            type: ChartType.Line
          }
    )
    return legends;
  };

  const renderTrendView = () => {
    return (
      <div className="h-full w-full flex flex-col">
        {selectedView === 'Chart' && (
          <ReMixChart
            data={trendChartData.data}
            xDataKey="date"
            isTimeSlot={false}
            yAxisLabel="Million MWh"
            yAxisWidth={50}
            isSmallLegend={true}
            unit="Million MWh"
            onlyTitle={true}
            chartContainerHeight='88%'
            nameGap={30}
            brushStart={BrushStart.End}
            showBrush={true}
            brushIndex={{
              startIndex: Math.max(0, trendChartData.data.length - 30),
              endIndex: trendChartData.data.length
            }}
            syncid={'consumption-generation-trend'}
            legends={getTrendLegends()}
          />
        )}
        {selectedView === 'Table' && (
          <ChartTable
            data={trendChartData.data}
            isSmall={true}
            legends={getTrendLegends()}
            unit="Million MWh"
            extraKeys={['date']}
            extraLabels={['Date']}
          />
        )}
        
        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-black bg-opacity-10 flex items-center justify-center z-10">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
              <p className="text-gray-700 text-sm font-medium"></p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSourceWiseView = () => {
    return (
      <div className="h-full w-full flex flex-col">
        {selectedView === 'Chart' && (
          <ReMixChart
            data={sourceWiseChartData.data}
            xDataKey="date"
            isTimeSlot={false}
            yAxisLabel="Million MWh"
            yAxisWidth={50}
            isSmallLegend={true}
            unit="Million MWh"
            onlyTitle={true}
            chartContainerHeight='88%'
            nameGap={30}
            brushStart={BrushStart.End}
            showBrush={true}
            brushIndex={{
              startIndex: Math.max(0, sourceWiseChartData.data.length - 30),
              endIndex: sourceWiseChartData.data.length
            }}
            syncid={'consumption-generation-source'}
            legends={getSourceWiseLegends()}
            customLegendOrder={['Solar', 'Wind', 'Hydro', 'Nuclear', 'Thermal', 'Total Consumption']}
          />
        )}
        {selectedView === 'Table' && (
          <ChartTable
            data={sourceWiseChartData.data}
            isSmall={true}
            legends={getSourceWiseLegends()}
            unit="Million MWh"
            extraKeys={['date']}
            extraLabels={['Date']}
          />
        )}
        
        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-black bg-opacity-10 flex items-center justify-center z-10">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
              <p className="text-gray-700 text-sm font-medium"></p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="text-left h-full w-full flex flex-col">
      <div className={`flex flex-row gap-x-2 ${isFullscreen ? 'justify-end' : 'justify-between'}`}>
        {!isFullscreen && (
          <h3 className="text-xl font-semibold text-gray-800 flex-shrink-0 chartHeading">
            Consumption & Generation
          </h3>
        )}
        <div className='flex flex-row gap-x-2 pb-2'>
          <FilterTab
            options={tabs}
            isSmall={!isFullscreen}
            selected={[selectedTab]}
            onClick={(tab) => setSelectedTab(tab)}
          />
          <FilterTab
            isSmall={!isFullscreen}
            options={views}
            selected={[selectedView]}
            onClick={(view) => setSelectedView(view)}
          />
          {!isFullscreen && (
            <button
              onClick={() => {
                const event = new CustomEvent('openFullscreen', { detail: 'card3' });
                window.dispatchEvent(event);
              }}
              className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100 ml-2"
              title="Open in fullscreen"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>
          )}
        </div>
      </div>
      
      {selectedTab === 'Trend ' && renderTrendView()}
      {selectedTab === 'Source-Wise' && renderSourceWiseView()}
    </div>
  );
}
